/**
 * QL-281 — Public flow Goal List renderer.
 *
 * Same view as the advisor list, fed by the token-based public goal hooks.
 * Goal add/delete go through the public goal endpoints (the same service the
 * advisor and the public API call); the add / edit form saves only on Save.
 */

import { pickT } from "@repo/shared";
import type { GoalTypeOption } from "@repo/shared/types";
import { apiErrorMessage } from "@/lib/api/_client";
import {
  useAddPublicGoal,
  useDeletePublicGoal,
  usePublicGoals,
  useUpdatePublicGoal,
} from "@/lib/hooks/use-public-flow/use-goals";
import type { GoalListConfig } from "@/lib/types/flow";
import {
  type GoalListGoal,
  GoalListView,
} from "@/pages/clients/detail/flows/instance/self-contained/goal-list";
import { T } from "./goal-list-renderer.i18n";

type PublicGoalListProps = {
  flowInstanceId: string;
  goalTypes: GoalTypeOption[];
  config: GoalListConfig;
  language: string;
  isCompleted: boolean;
  multiGoal: boolean;
  /** QL-188 — group answering note; null for non-group clients. */
  groupNote?: string | null;
  maxGoals: number;
};

export function PublicGoalList({
  flowInstanceId,
  goalTypes,
  config,
  language,
  isCompleted,
  multiGoal,
  groupNote = null,
  maxGoals,
}: PublicGoalListProps) {
  const t = pickT(T, language);
  const { goals, isLoading } = usePublicGoals();
  const addGoal = useAddPublicGoal();
  const updateGoal = useUpdatePublicGoal();
  const deleteGoal = useDeletePublicGoal();

  if (isLoading) return null;

  return (
    <GoalListView
      flowInstanceId={flowInstanceId}
      goals={(goals as GoalListGoal[]) ?? []}
      goalTypes={goalTypes}
      config={config}
      language={language}
      disabled={isCompleted}
      multiGoal={multiGoal}
      groupNote={groupNote}
      maxGoals={maxGoals}
      isPending={addGoal.isPending || updateGoal.isPending || deleteGoal.isPending}
      onCreate={async (data) => {
        try {
          await addGoal.mutateAsync({ ...data, lang: language });
          return { success: true };
        } catch (err) {
          return { success: false, error: apiErrorMessage(err, t.failedToCreateGoal) };
        }
      }}
      onUpdate={async (goalId, data) => {
        try {
          const saved = await updateGoal.mutateAsync({ goalId, data });
          return { success: true, platformSync: saved?.platformSync };
        } catch (err) {
          return { success: false, error: apiErrorMessage(err, t.failedToUpdateGoal) };
        }
      }}
      onDelete={async (goalId) => {
        try {
          await deleteGoal.mutateAsync(goalId);
          return { success: true };
        } catch (err) {
          return { success: false, error: apiErrorMessage(err, t.failedToDeleteGoal) };
        }
      }}
    />
  );
}
